import React, { useState, useEffect } from 'react';
import '../../orderFormSection.css';
import noImagePlaceholder from '../../assets/picture/noImagePlaceholder.png';

const formatDateTimeForInput = (date) => {
  return date.toISOString().slice(0, 16);
};

const getMinDateTime = () => {
  const now = new Date();
  return formatDateTimeForInput(now);
};

const getMaxDateTime = () => {
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  return formatDateTimeForInput(maxDate);
};

const formatDescription = (description) => {
  if (!description) return '';
  const toppingIndex = description.indexOf('Toppingi');
  return toppingIndex !== -1 ? description.substring(0, toppingIndex) + '...' : description;
};

const generateProductKey = (product) => {
  const toppingsKey = product.selectedToppings ?
    product.selectedToppings.map(t => t.id).sort().join('-') :
    'no-toppings';
  return `${product.id}-${toppingsKey}`;
};

const isLocationAvailableToday = (location) => {
  if (!location || location.accepting_orders !== "1") return false;
  return true;
};

const retryFetch = async (url, options, maxRetries = 5) => {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      retries++;
      if (retries === maxRetries) {
        throw new Error('Max retries reached');
      }
      await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, retries), 10000)));
    }
  }
};

export default function OrderFormSection() {
  const [orderType, setOrderType] = useState('individual');
  const [name, setName] = useState('');
  const [e_mail, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [pickupDateTime, setPickupDateTime] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Ramen');
  const [showProductDetails, setShowProductDetails] = useState(null);
  const [selectedLocationSchedule, setSelectedLocationSchedule] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [dateTimeError, setDateTimeError] = useState(null);
  const [suggestedDateTime, setSuggestedDateTime] = useState(null);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [showEmailError, setShowEmailError] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toppings, setToppings] = useState([]);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [privacyPolicyAgreed, setPrivacyPolicyAgreed] = useState(false);
  const [selectedLocationOrderTimes, setSelectedLocationOrderTimes] = useState(null);
  const [isAnyLocationAvailable, setIsAnyLocationAvailable] = useState(true);
  const [isSystemAvailable, setIsSystemAvailable] = useState(true);
  const [hasSetDateTime, setHasSetDateTime] = useState(false);

  const categoryTranslations = {
    'Ramen': 'Ramen',
    'Appetizer': 'Przystawka',
    'Drink': 'Napój',
  };

  const dayTranslations = {
    'Monday': 'Poniedziałek',
    'Tuesday': 'Wtorek',
    'Wednesday': 'Środa',
    'Thursday': 'Czwartek',
    'Friday': 'Piątek',
    'Saturday': 'Sobota',
    'Sunday': 'Niedziela',
  };

  const dayTranslationsLocative = {
    'Monday': 'poniedziałek',
    'Tuesday': 'wtorek',
    'Wednesday': 'środę',
    'Thursday': 'czwartek',
    'Friday': 'piątek',
    'Saturday': 'sobotę',
    'Sunday': 'niedzielę',
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const locationsData = await retryFetch(
          'https://wawaproxy.pl/7C6B5znXguTfp4XMk7QDTkggA2HKv2fHf5WCHShfxuKg4RZEseNVm3ph3uECf6yD',
          {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-Secret-Key': 'Q8xy68XKdyknRU26EDSHPGwvkhynQbTxRGjBVu7j3T7FbxeFPDpcwQXyhw9Pk2pmgehMDzfYtySwZdAWWy7wjDCJEwzsw65jHCxkkVWhCFFk0HjjVepaPXBzpev5mjJ40ASa8KgEKKuBKNNEGAg5d5NtPwXn5M0DZ0kDN9MedGBgPE1uGHkfCTmVw6f0VwmrPrE45HnJDarTp6MPshR4JHP70sruf8qaPdttW8BhgPsBQGqAk6vTYhrk1DpsQXqW'
            }
          }
        );
        setLocations(locationsData);
        const isAvailable = locationsData.some(location => isLocationAvailableToday(location));
        setIsAnyLocationAvailable(isAvailable);

        const productsData = await retryFetch(
          'https://wawaproxy.pl/VXeYKeB4YEF2p729fZEF4KZ5t9GypNrXQvH0DCgcKdsXH7d4hJcDkTDMuueeJr6H',
          {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-Secret-Key': 'vhMzh53MDMj2DTT51RWash0u4CyracVnuhf5NCR1aQwRfwP0A7ArKS1JFNjM2kVB2HF9UryhJpnBVphzPjRZu4QtDrqecFAGgVdmCGYAVkcf0jH3yK8kBggYWneur1aZm1wTXywhzmmc9Dact82qDybqDF6hrYb3KNUbFQ6a8BmuC3Gxk1hbnzVGZJz1tQhhHN7R7tTAz8bKTq6uf7kCZJzDuCCzyerwerAZrp1ujveg0AbXgCdwk5hww65ue35s'
            }
          }
        );
        setProducts(productsData);
        setCategories([...new Set(productsData.map(product => product.category))]);

        const toppingsData = await retryFetch(
          'https://wawaproxy.pl/8D40CX0T8hgfmchTAR5YFPg92jnrJwwFdyfeFFFBGjaH851aW8KxGaeCnPEzy2Vj',
          {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-Secret-Key': 'J8phyAJUR3sUxBbG4kQBZctPwRGG4z3V7BK8bw1F40Ae6Dzr5h2Je8Pn3uET1scGRDptw7U3vJ8YU7pFmuxcPzKsZAyAMSGWEzy7KxHx7Truk12q6cBveJgKfQMCp5UxPBHa9YaVdf72RetPkF2CeV5Ktw29HbwBnsUdHMP049zyAa222sadF87s7xKyF0JEYvK2vfPBhg5C50y2wH7DW7GQsDzEtUnfSU8ZexF2bbkXed04JB1t8QzPpbGU9Cqj'
            }
          }
        );
        setToppings(toppingsData);

      } catch (error) {
        console.error('Error fetching data:', error);
        setIsSystemAvailable(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=6Ld7rnkqAAAAAG3LhfC5bFnHE2KqraiqMbKmAZQS';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleAddProduct = (product) => {
    setSelectedProducts(prev => {
      const existingProductIndex = prev.findIndex(p =>
        p.id === product.id &&
        JSON.stringify(p.selectedToppings) === JSON.stringify(product.selectedToppings)
      );

      if (existingProductIndex >= 0) {
        return prev.map((p, index) =>
          index === existingProductIndex
            ? { ...p, quantity: p.quantity + product.quantity }
            : p
        );
      } else {
        return [...prev, { ...product }];
      }
    });
    setShowProductDetails(null);
    setSelectedToppings([]);
    document.body.style.overflow = 'auto';
  };

  const handleRemoveProduct = (productId, selectedToppings) => {
    const productKey = generateProductKey({ id: productId, selectedToppings });
    setSelectedProducts(prev => prev.filter(p =>
      generateProductKey(p) !== productKey
    ));
  };

  const handleQuantityChange = (productId, quantity, selectedToppings) => {
    const productKey = generateProductKey({ id: productId, selectedToppings });
    quantity = Math.min(1000, quantity);
    setSelectedProducts(prev => prev.map(p =>
      generateProductKey(p) === productKey ? { ...p, quantity } : p
    ));
  };

  const handleQuantityIncrement = (productId, selectedToppings) => {
    const productKey = generateProductKey({ id: productId, selectedToppings });
    setSelectedProducts(prev => prev.map(p =>
      generateProductKey(p) === productKey ?
        { ...p, quantity: Math.min(1000, p.quantity + 1) } : p
    ));
  };

  const handleQuantityDecrement = (productId, selectedToppings) => {
    const productKey = generateProductKey({ id: productId, selectedToppings });
    setSelectedProducts(prev => prev.map(p =>
      generateProductKey(p) === productKey ?
        { ...p, quantity: Math.max(1, p.quantity - 1) } : p
    ));
  };

  const handleQuantityBlur = (productId, quantity) => {
    if (isNaN(quantity) || quantity < 1) {
      quantity = 1;
    } else if (quantity > 1000) {
      quantity = 1000;
    }
    setSelectedProducts(prev => prev.map(p => p.id === productId ? { ...p, quantity } : p));
  };

  const getTotalPrice = (product) => {
    const basePrice = parseFloat(product.price) * product.quantity;
    const toppingsPrice = product.selectedToppings?.reduce((sum, topping) =>
      sum + (parseFloat(topping.price) * product.quantity), 0) || 0;
    const packagingPrice = product.packaging ? parseFloat(product.packaging.price) * product.quantity : 0;
    return basePrice + toppingsPrice + packagingPrice;
  };

  const totalPrice = selectedProducts.reduce((sum, product) => sum + getTotalPrice(product), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const token = await window.grecaptcha.execute('6Ld7rnkqAAAAAG3LhfC5bFnHE2KqraiqMbKmAZQS', { action: 'submit' });

      const orderData = {
        orderType,
        name,
        e_mail,
        phone,
        pickupDateTime,
        pickupLocation,
        selectedProducts: selectedProducts.map(({ name, image, description, price, category, default_packaging, default_packaging_id, packaging, selectedToppings, ...rest }) => ({
          ...rest,
          selectedToppings: selectedToppings ? selectedToppings.map(t => t.id) : []
        })),
        captchaToken: token
      };

      const response = await fetch(
        'https://wawaproxy.pl/58AwWnQpUye3sXCwaEw8WC5smmQbhv9WDCKDC5jhfAbtzyYVz0NGcZGuA6bAEJ9S',
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Secret-Key': 'kw8BX7n5rrUpcz7vfer5HERrtdTKPrCAjEntYPjBwwrVSqMbBjgwp0KbYqBPemFtTrHkpGCBEEnBcs5nfgCUzffGvxBkg23Xw42Cw4E2VSxZbqbVaDvkPG0Q2KQUzAmSAyQy8PtWkHv6SgVDrkKsC43qM8DGEMJE4sVAt4nk5tHbZ8DGUPkc5z8WqXFkpb5yDKB7pSCedsjUuM3wzdwE2fjygq0ZvPxFrdZcPSBasWs5q2K67zbd1rqsMKxn3uMM'
          },
          body: JSON.stringify(orderData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'error') {
        let errorMessage = 'Wystąpił błąd podczas składania zamówienia: ';

        switch (data.message) {
          case 'Invalid JSON data':
            errorMessage += 'Nieprawidłowy format danych. Proszę odświeżyć stronę i spróbować ponownie.';
            break;
          case 'Captcha token is required':
          case 'Invalid captcha verification':
            errorMessage += 'Weryfikacja bezpieczeństwa nie powiodła się. Proszę odświeżyć stronę i spróbować ponownie.';
            break;
          case 'Invalid name format':
            errorMessage += 'Imię/nazwa firmy może zawierać tylko litery, cyfry, spacje i myślniki (2-50 znaków).';
            break;
          case 'Invalid email format':
            errorMessage += 'Proszę podać prawidłowy adres e-mail.';
            break;
          case 'Invalid phone number format':
            errorMessage += 'Numer telefonu musi zawierać od 9 do 15 cyfr.';
            break;
          case 'Invalid order type':
            errorMessage += 'Nieprawidłowy typ zamówienia. Proszę wybrać poprawny typ.';
            break;
          case 'Invalid date format':
            errorMessage += 'Nieprawidłowy format daty i godziny odbioru.';
            break;
          case 'Missing required fields':
            errorMessage += 'Proszę wypełnić wszystkie wymagane pola.';
            break;
          case 'Pickup time cannot be in the past':
            errorMessage += 'Data i godzina odbioru nie może być z przeszłości.';
            break;
          case 'Invalid pickup location':
            errorMessage += 'Wybrana lokalizacja jest nieprawidłowa. Proszę wybrać inną.';
            break;
          case 'Pickup location is closed on the selected day':
            errorMessage += 'Lokal jest zamknięty w wybranym dniu.';
            break;
          case 'Pickup time is outside of allowed pickup hours':
            errorMessage += 'Wybrana godzina odbioru jest poza godzinami pracy lokalu.';
            break;
          case 'No products selected':
            errorMessage += 'Proszę wybrać produkty do zamówienia.';
            break;
          case 'Invalid product IDs':
            errorMessage += 'Niektóre z wybranych produktów są niedostępne. Proszę odświeżyć stronę i spróbować ponownie.';
            break;
          case 'Unauthorized access':
            errorMessage += 'Błąd autoryzacji. Proszę odświeżyć stronę i spróbować ponownie.';
            break;
          case 'Failed to create order':
            errorMessage += 'Nie udało się utworzyć zamówienia. Proszę spróbować ponownie za chwilę.';
            break;
          case 'This location is not accepting orders at this time':
            errorMessage += 'Ten lokal tymczasowo nie przyjmuje zamówień online. Proszę wybrać inny lokal lub spróbować później.';
            break;
          case 'Pickup time does not allow for required preparation time':
            if (data.closestAvailableTime) {
              errorMessage += `Zamówienie wymaga więcej czasu na przygotowanie. Najbliższy możliwy termin odbioru to: ${new Date(data.closestAvailableTime).toLocaleString(undefined, {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}.`;
            } else {
              errorMessage += 'Zamówienie wymaga więcej czasu na przygotowanie. Proszę wybrać późniejszą godzinę.';
            }
            break;
          case 'No available pickup time on the selected day':
            errorMessage += 'Brak dostępnych terminów odbioru w wybranym dniu. Proszę wybrać inny dzień.';
            break;
          case 'Orders can only be placed between these hours':
            errorMessage = `Zamówienia można składać tylko w godzinach ${data.startTime} - ${data.endTime}`;
            break;
          case 'Pickup time must be within these hours':
            var dayTranslation = dayTranslationsLocative[data.dayOfWeek] || data.dayOfWeek;
            errorMessage = `Odbiór zamówienia w ${dayTranslation.toLowerCase()} możliwy jest tylko w godzinach ${data.startTime} - ${data.endTime}`;
            break;
          case 'Orders are not accepted on':
            var dayTranslation = dayTranslationsLocative[data.dayOfWeek] || data.dayOfWeek;
            errorMessage = `Nie przyjmujemy zamówień w ${dayTranslation.toLowerCase()}.`;
            break;
          case 'Orders cannot be placed at this time':
            errorMessage = 'Nie można składać zamówień w tej chwili.';
            break;
          case 'Orders cannot be placed today':
            errorMessage = 'Nie można składać dzisiaj zamówień.';
            break;
          case 'Missing required fields for catering order':
            errorMessage += 'Proszę wypełnić wszystkie wymagane pola dla zamówienia cateringowego.';
            break;
          case 'Cannot place order for':
            var dayTranslation = dayTranslations[data.dayOfWeek] || data.dayOfWeek;
            errorMessage += `Nie można złożyć zamówienia na ${dayTranslation.toLowerCase()}.`;
            break;

          case 'Location is closed on':
            var dayTranslation = dayTranslations[data.dayOfWeek] || data.dayOfWeek;
            errorMessage += `Lokal jest zamknięty w ${dayTranslation.toLowerCase()}.`;
            break;

          case 'Orders are not accepted for':
            var dayTranslation = dayTranslationsLocative[data.dayOfWeek] || data.dayOfWeek;
            errorMessage += `Nie przyjmujemy zamówień na ${dayTranslation.toLowerCase()}.`;
            break;

          case 'One of the selected products is unavailable':
            errorMessage += 'Jeden z wybranych produktów jest obecnie niedostępny.';
            break;

          case 'Invalid topping ID:':
            errorMessage += 'Jeden z wybranych dodatków jest niedostępny. Proszę odświeżyć stronę i spróbować ponownie.';
            break;
          default:
            errorMessage += data.message;
        }

        setSubmitError(errorMessage);
        return;
      } else {
        setOrderId(data.orderId);
        setSubmitSuccess(true);
        setShowSummary(false);
      }

    } catch (error) {
      setSubmitError('System zamówień jest obecnie niedostępny. Prosimy spróbować później.');
      setIsSystemAvailable(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowProductDetails = (product, event) => {
    if (event) {
      event.preventDefault();
    }

    const scrollPosition = window.pageYOffset;

    setShowProductDetails({
      ...product,
      quantity: 1,
      selectedToppings: product.category === 'Ramen' ? [] : null
    });
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  };

  const handleCloseProductDetails = () => {
    setShowProductDetails(null);
    document.body.style.overflow = 'auto';
  };

  const handleDetailQuantityChange = (quantity) => {
    quantity = Math.min(1000, quantity);
    setShowProductDetails(prev => ({ ...prev, quantity }));
  };

  const handleDetailQuantityBlur = (quantity) => {
    if (isNaN(quantity) || quantity < 1) {
      quantity = 1;
    } else if (quantity > 1000) {
      quantity = 1000;
    }
    setShowProductDetails(prev => ({ ...prev, quantity }));
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    const location = locations.find(loc => loc.id === locationId);

    setPickupLocation(locationId);
    setSelectedLocationSchedule(location ? location.schedule : []);

    if (location && location.start_order_allow_time && location.end_order_allow_time) {
      setSelectedLocationOrderTimes({
        start_order_allow_time: location.start_order_allow_time,
        end_order_allow_time: location.end_order_allow_time
      });
    } else {
      setSelectedLocationOrderTimes(null);
    }
  };

  useEffect(() => {
    if (pickupLocation && pickupDateTime) {
      validateDateTime(pickupDateTime);
      validateForm();
    }
  }, [pickupLocation, selectedLocationSchedule]);

  const groupScheduleByTime = (schedule) => {
    const grouped = {};
    schedule.forEach(({ day_of_week, pickup_start_time, pickup_end_time, closed }) => {
      const key = closed === "1" ? "Closed" : `${pickup_start_time}-${pickup_end_time}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(dayTranslations[day_of_week]);
    });
    return grouped;
  };

  const formatGroupedSchedule = (groupedSchedule) => {
    const formatted = [];
    Object.entries(groupedSchedule).forEach(([time, days]) => {
      if (days.length > 1) {
        formatted.push(`${days[0]} - ${days[days.length - 1]}: ${time === "Closed" ? "Zamknięte" : time.replace('-', ' - ')}`);
      } else {
        formatted.push(`${days[0]}: ${time === "Closed" ? "Zamknięte" : time.replace('-', ' - ')}`);
      }
    });
    return formatted;
  };

  const findClosestTime = (selectedDate, schedule) => {
    const selectedTime = selectedDate.getHours() * 60 + selectedDate.getMinutes();
    const startTime = schedule.pickup_start_time.split(':').map(Number);
    const endTime = schedule.pickup_end_time.split(':').map(Number);
    const startMinutes = startTime[0] * 60 + startTime[1];
    const endMinutes = endTime[0] * 60 + endTime[1];

    if (selectedTime < startMinutes || selectedTime > endMinutes) {
      const distanceToStart = Math.abs(selectedTime - startMinutes);
      const distanceToEnd = Math.abs(selectedTime - endMinutes);

      const closestTime = new Date(selectedDate);
      if (distanceToStart <= distanceToEnd) {
        closestTime.setHours(startTime[0], startTime[1], 0);
      } else {
        closestTime.setHours(endTime[0], endTime[1], 0);
      }
      return closestTime;
    }
    return null;
  };

  const findNextAvailableDateTime = (selectedDate) => {
    let testDate = new Date(selectedDate);
    let found = false;
    let attempts = 0;
    let closestDateTime = null;
    let minTimeDiff = Infinity;

    const currentDayName = testDate.toLocaleString('en-US', { weekday: 'long' });
    const currentSchedule = selectedLocationSchedule.find(s => s.day_of_week === currentDayName);

    if (currentSchedule && currentSchedule.closed !== "1") {
      const closestTime = findClosestTime(testDate, currentSchedule);
      if (closestTime) {
        closestDateTime = closestTime;
        found = true;
      }
    }

    while (!found && attempts < 7) {
      testDate.setDate(testDate.getDate() + 1);
      const dayName = testDate.toLocaleString('en-US', { weekday: 'long' });
      const schedule = selectedLocationSchedule.find(s => s.day_of_week === dayName);

      if (schedule && schedule.closed !== "1") {
        const suggestedTime = new Date(testDate);
        suggestedTime.setHours(
          parseInt(schedule.pickup_start_time.split(':')[0]),
          parseInt(schedule.pickup_start_time.split(':')[1]),
          0
        );

        const timeDiff = Math.abs(suggestedTime - selectedDate);
        if (timeDiff < minTimeDiff) {
          minTimeDiff = timeDiff;
          closestDateTime = suggestedTime;
        }
      }
      attempts++;
    }

    if (closestDateTime) {
      setSuggestedDateTime(closestDateTime);
    }
  };

  const validateDateTime = (dateTime) => {
    if (pickupLocation) {
      const selectedLocation = locations.find(loc => loc.id === pickupLocation);
      if (selectedLocation?.accepting_orders === "0") {
        setDateTimeError('Ten lokal tymczasowo nie przyjmuje zamówień online.');
        return false;
      }
    }

    if (!dateTime || !pickupLocation || !dateTime.includes('T') || dateTime.split('T')[1] === '') {
      return false;
    }

    const selectedDate = new Date(dateTime);
    const now = new Date();
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);

    if (selectedDate < now) {
      setDateTimeError('Nie można wybrać daty z przeszłości');
      return false;
    }

    if (selectedDate > maxDate) {
      setDateTimeError('Nie można wybrać daty późniejszej niż rok od dziś');
      return false;
    }

    const selectedDay = selectedDate.toLocaleString('en-US', { weekday: 'long' });
    const selectedTime = selectedDate.toTimeString().split(' ')[0];

    if (!dayTranslationsLocative[selectedDay]) {
      console.error(`Missing translation for day: ${selectedDay}`);
      setDateTimeError('Wystąpił błąd z formatem daty. Proszę spróbować ponownie.');
      return false;
    }

    const schedule = selectedLocationSchedule.find(s => s.day_of_week === selectedDay);

    if (!schedule || schedule.closed === "1") {
      findNextAvailableDateTime(selectedDate);
      setDateTimeError(`Lokal jest zamknięty w ${dayTranslationsLocative[selectedDay].toLowerCase()}.`);
      return false;
    }

    if (selectedTime < schedule.pickup_start_time || selectedTime > schedule.pickup_end_time) {
      findNextAvailableDateTime(selectedDate);
      setDateTimeError(`Dostępne godziny odbioru w ${dayTranslationsLocative[selectedDay].toLowerCase()} to: ${schedule.pickup_start_time} - ${schedule.pickup_end_time}`);
      return false;
    }

    setDateTimeError(null);
    setSuggestedDateTime(null);
    return true;
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsEmailValid(validateEmail(newEmail));
    setShowEmailError(false);
  };

  const handleEmailBlur = () => {
    setShowEmailError(true);
  };

  const validatePhone = (phone) => {
    const digitCount = phone.replace(/\D/g, '').length;
    return digitCount >= 9;
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
    setIsPhoneValid(validatePhone(newPhone));
    setShowPhoneError(false);
  };

  const handlePhoneBlur = () => {
    setShowPhoneError(true);
  };

  const validateForm = () => {
    if (orderType === 'catering') {
      const isValid = name.trim() !== '' && isEmailValid && isPhoneValid;
      setIsFormValid(isValid);
      return isValid;
    }

    let isDateTimeValid = true;
    if (pickupDateTime && pickupLocation) {
      isDateTimeValid = validateDateTime(pickupDateTime);
      if (!hasSetDateTime) setHasSetDateTime(true);
    }

    const selectedLocation = locations.find(loc => loc.id === pickupLocation);
    const isValid =
      name.trim() !== '' &&
      isEmailValid &&
      isPhoneValid &&
      pickupLocation !== '' &&
      (selectedLocation?.accepting_orders === "1") &&
      pickupDateTime !== '' &&
      selectedProducts.length > 0 &&
      isDateTimeValid &&
      (orderType === 'catering' || isWithinOrderHours());

    setIsFormValid(isValid);
    return isValid;
  };

  useEffect(() => {
    validateForm();
    setSubmitError(null);
  }, [name, e_mail, phone, pickupLocation, pickupDateTime, selectedProducts]);

  const groupedSchedule = groupScheduleByTime(selectedLocationSchedule);
  const formattedSchedule = formatGroupedSchedule(groupedSchedule);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    const currentTime = pickupDateTime ? pickupDateTime.split('T')[1] : '';

    const newDateTime = `${newDate}T${currentTime}`;
    setPickupDateTime(newDateTime);

    if (currentTime && hasSetDateTime && pickupLocation) {
      validateDateTime(newDateTime);
    }
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    const currentDate = pickupDateTime ? pickupDateTime.split('T')[0] : new Date().toISOString().split('T')[0];
    const newDateTime = `${currentDate}T${newTime}`;
    setPickupDateTime(newDateTime);
    
    if (newTime && currentDate && pickupLocation) {
      setHasSetDateTime(true);
      validateDateTime(newDateTime);
    }
  };

  const handleShowSummary = () => {
    const scrollPosition = window.pageYOffset;
    setShowSummary(true);
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(() => {
      const productsContainer = document.querySelector('.summary-products');
      if (productsContainer) {
        productsContainer.scrollTop = 0;
      }
      window.scrollTo(0, scrollPosition);
    });
  };

  const handleCloseSummary = () => {
    setShowSummary(false);
    document.body.style.overflow = 'auto'; 
  };

  const handleToppingToggle = (topping) => {
    setShowProductDetails(prev => {
      const currentToppings = prev.selectedToppings || [];
      const isToppingSelected = currentToppings.some(t => t.id === topping.id);

      const newToppings = isToppingSelected
        ? currentToppings.filter(t => t.id !== topping.id)
        : [...currentToppings, topping];

      return {
        ...prev,
        selectedToppings: newToppings
      };
    });
  };

  const renderToppingsSection = () => {
    if (showProductDetails?.category !== 'Ramen') {
      return null;
    }

    const selectedToppings = showProductDetails.selectedToppings || [];

    return (
      <div className="toppings-section">
        <h4>Dodatki do ramenu:</h4>
        <div className="toppings-list">
          {toppings.map(topping => (
            <div key={topping.id} className="topping-item">
              <label>
                <input
                  type="checkbox"
                  checked={selectedToppings.some(t => t.id === topping.id)}
                  onChange={() => handleToppingToggle(topping)}
                />
                <span>{topping.name}</span>
                <span className="topping-price">+{parseFloat(topping.price).toFixed(2)}&nbsp;zł</span>
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };


  const handleOrderTypeChange = (e) => {
    setOrderType(e.target.value);
    if (e.target.value === 'catering') {
      setPickupLocation('');
      setPickupDateTime('');
      setSelectedProducts([]);
    }
  };

  const renderProductDetails = () => {
    if (!showProductDetails) return null;

    return (
      <div className="product-details-overlay" onClick={handleCloseProductDetails}>
        <div className="product-details" onClick={(e) => e.stopPropagation()}>
          <img
            src={showProductDetails.image || noImagePlaceholder}
            alt={showProductDetails.name}
          />
          <h4>{showProductDetails.name}</h4>
          <p id='product-details-details-text'>{showProductDetails.description}</p>
          {renderToppingsSection()}
          {showProductDetails.packaging && (
            <div className="packaging-info">
              <span>Opakowanie: </span>
              <span className="packaging-price"> +{parseFloat(showProductDetails.packaging.price).toFixed(2)} zł</span>
            </div>
          )}
          <div className="price-amount">
            <label>Ilość:</label>
            <div className="quantity-controls">
              <span className="input-wrapper">
                <button type="button" id="decrement" onClick={() => handleDetailQuantityChange(Math.max(1, showProductDetails.quantity - 1))}>-</button>
                <input
                  type="number"
                  value={showProductDetails.quantity}
                  onChange={(e) => handleDetailQuantityChange(parseInt(e.target.value))}
                  onBlur={(e) => handleDetailQuantityBlur(parseInt(e.target.value))}
                  min="1"
                  max="1000"
                  id="quantity"
                />
                <button type="button" id="increment" onClick={() => handleDetailQuantityChange(showProductDetails.quantity + 1)}>+</button>
              </span>
            </div>
            <p>Cena: <b>&nbsp;{getTotalPrice(showProductDetails).toFixed(2)} zł</b></p>
          </div>
          <div className="button-group">
            <button type="button" className="add-button" onClick={() => handleAddProduct(showProductDetails)}>Dodaj</button>
            <button type="button" className="cancel-button" onClick={handleCloseProductDetails}>Anuluj</button>
          </div>
        </div>
      </div>
    );
  };

  const preloadTestData = () => {
    setName('Test User');
    setEmail('testuser@example.com');
    setPhone('123456789');
    setPickupDateTime('2025-02-08T18:00');
    setPickupLocation(2);
    setSelectedProducts([
      {
        id: '1',
        name: 'Cream on',
        price: '37.00',
        quantity: 2,
        selectedToppings: [{ id: '1', name: 'Topping 1', price: '2.00' }],
        packaging: { name: 'średnie', price: '3.50' }
      },
      {
        id: '3',
        name: 'Meet your hunger',
        price: '37.00',
        quantity: 1,
        selectedToppings: [{ id: '2', name: 'Topping 2', price: '3.00' }],
        packaging: { name: 'duże', price: '4.00' }
      },
      {
        id: '5',
        name: 'Kimchi',
        price: '13.00',
        quantity: 3,
        selectedToppings: [],
        packaging: { name: 'małe', price: '2.00' }
      },
      {
        id: '7',
        name: 'Cola puszka(330 ml)',
        price: '8.00',
        quantity: 2,
        selectedToppings: [],
        packaging: { name: 'brak', price: '0.00' }
      }
    ]);
  };

  const renderLocationOptions = () => {
    const now = new Date();
    const currentDay = now.toLocaleString('en-US', { weekday: 'long' });
    const currentTime = now.getHours() * 60 + now.getMinutes();

    return [
      <option key="default" value="" disabled={pickupLocation !== ""}>
        Wybierz miejsce
      </option>,
      ...locations.map(location => {
        const isAcceptingOrders = location.accepting_orders === "1";
        const todaySchedule = location.schedule?.find(s => s.day_of_week === currentDay);
        const hasOrderHours = todaySchedule?.start_order_allow_time && todaySchedule?.end_order_allow_time;

        let isWithinOrderHours = false;
        if (hasOrderHours) {
          const [startHours, startMinutes] = todaySchedule.start_order_allow_time.split(':').map(Number);
          const [endHours, endMinutes] = todaySchedule.end_order_allow_time.split(':').map(Number);
          const startTime = startHours * 60 + startMinutes;
          const endTime = endHours * 60 + endMinutes;
          isWithinOrderHours = currentTime >= startTime && currentTime <= endTime;
        }

        if (orderType === 'catering') {
          return (
            <option key={location.id} value={location.id}>
              {location.address}
            </option>
          );
        }

        let statusInfo = '';
        let isDisabled = false;

        if (!isAcceptingOrders) {
          statusInfo = ' (Tymczasowo nie przyjmuje zamówień)';
          isDisabled = true;
        } else if (hasOrderHours) {
          const startTime = todaySchedule.start_order_allow_time.split(':').slice(0, 2).join(':');
          const endTime = todaySchedule.end_order_allow_time.split(':').slice(0, 2).join(':');
          if (!isWithinOrderHours)
            statusInfo = ` (Lokal przyjmuje zamówienia w godzinach: ${startTime}-${endTime})`;
          isDisabled = !isWithinOrderHours;
        } else {
          statusInfo = ' (Dziś nie przyjmuje zamówień)';
          isDisabled = true;
        }

        return (
          <option
            key={location.id}
            value={location.id}
            disabled={isDisabled}
          >
            {location.address}{statusInfo}
          </option>
        );
      })
    ];
  };

  const WarningsContainer = () => {
    if (orderType === 'individual' && !isAnyLocationAvailable) {
      return (
        <div className="warnings-container" style={{ margin: '20px 0' }}>
          <div className="order-hours-message" style={{
            padding: '10px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeeba',
            borderRadius: '4px',
            color: '#856404'
          }}>
            Wszystkie lokale są dziś nieczynne lub nie przyjmują zamówień indywidualnych online. <strong>Zamówienia cateringowe są nadal dostępne.</strong>
          </div>
        </div>
      );
    }
    return null;
  };

  const isWithinOrderHours = () => {
    if (orderType === 'catering') return true;

    const currentLocation = locations.find(loc => loc.id === pickupLocation);
    if (!currentLocation?.schedule) return false;

    const now = new Date();
    const currentDay = now.toLocaleString('en-US', { weekday: 'long' });
    const todaySchedule = currentLocation.schedule.find(s => s.day_of_week === currentDay);

    if (!todaySchedule ||
      !todaySchedule.start_order_allow_time ||
      !todaySchedule.end_order_allow_time) {
      return false;
    }

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [startHours, startMinutes] = todaySchedule.start_order_allow_time.split(':').map(Number);
    const [endHours, endMinutes] = todaySchedule.end_order_allow_time.split(':').map(Number);

    const startTime = startHours * 60 + startMinutes;
    const endTime = endHours * 60 + endMinutes;

    return currentTime >= startTime && currentTime <= endTime;
  };

  const isAnyLocationAvailableNow = () => {
    if (orderType === 'catering') return true;

    const now = new Date();
    const currentDay = now.toLocaleString('en-US', { weekday: 'long' });

    return locations.some(location => {
      if (location.accepting_orders !== "1") return false;

      const todaySchedule = location.schedule.find(s => s.day_of_week === currentDay);
      if (!todaySchedule ||
        !todaySchedule.start_order_allow_time ||
        !todaySchedule.end_order_allow_time) {
        return false;
      }

      const currentTime = now.getHours() * 60 + now.getMinutes();
      const [startHours, startMinutes] = todaySchedule.start_order_allow_time.split(':').map(Number);
      const [endHours, endMinutes] = todaySchedule.end_order_allow_time.split(':').map(Number);

      const startTime = startHours * 60 + startMinutes;
      const endTime = endHours * 60 + endMinutes;

      return currentTime >= startTime && currentTime <= endTime;
    });
  };

  const disabledSectionStyle = {
    opacity: 0.5,
    pointerEvents: 'none',
    position: 'relative'
  };

  return (
    <section id="orderFormSection">
      {!isSystemAvailable ? (
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '5px',
          color: '#721c24',
          textAlign: 'center',
          margin: '20px 0'
        }}>
          <h2>System zamówień jest obecnie niedostępny</h2>
          <p>Przepraszamy za utrudnienia. Prosimy spróbować później lub skontaktować się z nami telefonicznie.</p>
        </div>
      ) : (
        <form onSubmit={(e) => e.preventDefault()}>
          <div id='orderFormSectionInformation'>
            <h1>Zamów online i odbierz w lokalu</h1>
            <p>Wypełnij formularz zamówienia, a my przygotujemy Twoje zamówienie do odbioru w wybranym lokalu o wybranej przez Ciebie godzinie.</p>
          </div>

          <div className="form-section">
            <h2>Dane użytkownika:</h2>
            <div>
              <label>
                Imię/Firma:
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </label>
            </div>
            <div>
              <label>
                E-mail:
                <input
                  type="text"
                  value={e_mail}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  required
                  style={{ borderColor: showEmailError && !isEmailValid && e_mail ? '#dc3545' : undefined }}
                />
              </label>
              {showEmailError && !isEmailValid && e_mail && (
                <div className="email-error">
                  Proszę podać prawidłowy adres e-mail
                </div>
              )}
              <div className="email-hint">
                Na swój adres e-mail otrzymasz potwierdzenie przyjęcia zamówienia.
              </div>
            </div>
            <div>
              <label>
                Numer telefonu:
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  required
                  style={{ borderColor: showPhoneError && !isPhoneValid && phone ? '#dc3545' : undefined }}
                />
              </label>
              {showPhoneError && !isPhoneValid && phone && (
                <div className="phone-hint">
                  Numer telefonu musi zawierać minimum 9 cyfr
                </div>
              )}
            </div>
            <div>
              <label>
                Typ zamówienia:
                <select value={orderType} onChange={handleOrderTypeChange}>
                  <option value="individual">Zamówienie indywidualne</option>
                  <option value="catering">Catering grupowy (20+ osób)</option>
                </select>
              </label>
            </div>
          </div>

          {orderType === 'catering' && (
            <div className="catering-hint">
              W przypadku zamówienia cateringowego nie wybierasz daty, lokalizacji ani produktów.
              Po złożeniu formularza nasz personel skontaktuje się z Tobą mailowo w celu omówienia szczegółów zamówienia.
            </div>
          )}



          <div className={`form-section`} style={orderType === 'catering' ? disabledSectionStyle : {}}>
            <h2>Data i miejsce odbioru:</h2>
            <div>
              <label>
                Miejsce odbioru:
                <select value={pickupLocation} onChange={handleLocationChange} required>
                  {renderLocationOptions()}
                </select>
              </label>
            </div>
            <WarningsContainer />
            <br></br>
            <div className="datetime-picker-container">
              <div className="date-picker-wrapper">
                <label>Data odbioru:</label>
                <input
                  type="date"
                  className="date-picker"
                  value={pickupDateTime ? pickupDateTime.split('T')[0] : ''}
                  onChange={handleDateChange}
                  min={getMinDateTime().split('T')[0]}
                  max={getMaxDateTime().split('T')[0]}
                  required
                />
              </div>
              <div className="time-picker-wrapper">
                <label>Godzina odbioru:</label>
                <input
                  type="time"
                  className="time-picker"
                  value={pickupDateTime ? pickupDateTime.split('T')[1] : ''}
                  onChange={handleTimeChange}
                  required
                />
              </div>
            </div>
            {dateTimeError && (
              <div className="date-hint">{dateTimeError}</div>
            )}
            {suggestedDateTime && (
              <div className="date-suggestion">
                Sugerowana najbliższa dostępna data i godzina: {suggestedDateTime.toLocaleString()}
              </div>
            )}
            {selectedLocationSchedule.length > 0 && (
              <div className="schedule-hint">
                <h4>Dostępne godziny odbioru:</h4>
                <ul>
                  {formattedSchedule.map((schedule, index) => (
                    <li key={index}>{schedule}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="form-section" style={orderType === 'catering' ? disabledSectionStyle : {}}>
            <h2>Wybierz produkty:</h2>
            <div className="category-tabs-container">
              <span className="category-tabs-label">Wybierz kategorię:</span>
              <div className="category-tabs">
                {categories.map(category => (
                  <button
                    type="button"
                    key={category}
                    className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {categoryTranslations[category] || category}
                  </button>
                ))}
              </div>
            </div>

            <div className="product-list">
              {products.filter(product => product.category === selectedCategory).map(product => (
                <div key={product.id} className="product-item">
                  <img
                    src={product.image || noImagePlaceholder}
                    alt={product.name}
                    className="product-image"
                  />
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    {product.description && <p className="product-description">{formatDescription(product.description)}</p>}
                    {product.packaging && (
                      <div className="packaging-info">
                        <span>Opakowanie: </span>
                        <span className="packaging-price"> +{parseFloat(product.packaging.price).toFixed(2)} zł</span>
                      </div>
                    )}
                    <div className="product-footer">
                      <p>{parseFloat(product.price).toFixed(2)} zł</p>
                      <button
                        type="button"
                        className="select-button"
                        onClick={(e) => handleShowProductDetails(product, e)}
                      >
                        Wybierz
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {renderProductDetails()}

          <div className="form-section">
            <h2>Wybrane produkty:</h2>
            {selectedProducts.map(product => (
              <div key={generateProductKey(product)} className="selected-product">
                <h4>{product.name}</h4>
                {product.selectedToppings?.length > 0 && (
                  <div className="selected-toppings">
                    <small>Dodatki: {product.selectedToppings.map(t => t.name).join(', ')}</small>
                  </div>
                )}
                {product.packaging && (
                  <div className="selected-toppings">
                    <small>Opakowanie: +{parseFloat(product.packaging.price).toFixed(2)} zł</small>
                  </div>
                )}
                <div className="product-details-row">
                  <span>Ilość:</span>
                  <div className="quantity-controls">
                    <span className="input-wrapper">
                      <button type="button" id="decrement" onClick={() => handleQuantityDecrement(product.id, product.selectedToppings)}>-</button>
                      <input
                        type="number"
                        value={product.quantity}
                        onChange={(e) => handleQuantityChange(product.id, parseInt(e.target.value), product.selectedToppings)}
                        onBlur={(e) => handleQuantityBlur(product.id, parseInt(e.target.value), product.selectedToppings)}
                        min="1"
                        max="1000"
                        id="quantity"
                      />
                      <button type="button" id="increment" onClick={() => handleQuantityIncrement(product.id, product.selectedToppings)}>+</button>
                    </span>
                  </div>
                  <p>Cena:&nbsp;{getTotalPrice(product).toFixed(2)} zł</p>
                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => handleRemoveProduct(product.id, product.selectedToppings)}
                  >
                    Usuń
                  </button>
                </div>
              </div>
            ))}
            <h3>Łączna cena: {totalPrice.toFixed(2)} zł</h3>
          </div>
          <button
            type="button"
            className="submit-button"
            onClick={handleShowSummary}
            disabled={!isFormValid}
          >
            {orderType === 'catering' ? 'Wyślij zapytanie' : 'Zobacz podsumowanie'}
          </button>

          {/* <button type="button" className="preload-button" onClick={preloadTestData}>
            Preload Test Data
          </button> */}
        </form>
      )}

      {showSummary && (
        <div className="product-details-overlay">
          <div className="product-details summary-popup">
            <h2 id='summary-h2' style={{ borderBottom: '1px solid #999999', paddingBottom: '5px', marginBottom: '20px', textAlign: 'center' }}>
              {orderType === 'catering' ? 'Potwierdzenie wysłania zapytania' : 'Podsumowanie zamówienia'}
            </h2>

            <div className="summary-section" style={{ borderBottom: orderType === 'catering' ? 'none' : '1px solid #999999' }}>
              <h3>Dane zamawiającego:</h3>
              <p><strong>Imię/Firma:</strong> {name}</p>
              <p><strong>E-mail:</strong> {e_mail}</p>
              <p><strong>Telefon:</strong> {phone}</p>
              <p><strong>Typ zamówienia:</strong> {orderType === 'individual' ? 'Zamówienie indywidualne' : 'Catering grupowy'}</p>
            </div>

            {orderType !== 'catering' && (
              <>
                <div className="summary-section">
                  <h3>Szczegóły odbioru:</h3>
                  <p><strong>Lokalizacja:</strong> {locations.find(loc => loc.id === pickupLocation)?.address}</p>
                  <p><strong>Data i godzina:</strong> {new Date(pickupDateTime).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                </div>

                <div className="summary-section">
                  <h3>Wybrane produkty:</h3>
                  <div className="summary-products">
                    {selectedProducts.map(product => (
                      <div key={generateProductKey(product)} className="summary-product-item">
                        <div className="summary-product-main">
                          <span className="summary-product-name">
                            {product.name}
                            <span className="summary-product-base-price">
                              ({parseFloat(product.price).toFixed(2)} zł)
                            </span>
                          </span>

                          {product.selectedToppings?.length > 0 && (
                            <div className="summary-product-toppings">
                              <div>Dodatki:</div>
                              <div className="summary-product-toppings-list">
                                {product.selectedToppings.map(topping => (
                                  <div key={topping.id} className="summary-product-toppings-item">
                                    <span>{topping.name}</span>
                                    <span>+{parseFloat(topping.price).toFixed(2)} zł</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {product.packaging && (
                            <div className="summary-product-toppings">
                              <div>Opakowanie:</div>
                              <div className="summary-product-toppings-list">
                                <div className="summary-product-toppings-item">
                                  <span>+{parseFloat(product.packaging.price).toFixed(2)} zł</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <span className="summary-product-quantity">
                          × {product.quantity}
                        </span>

                        <span className="summary-product-total">
                          {getTotalPrice(product).toFixed(2)} zł
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="scroll-indicator">Przewiń, aby zobaczyć więcej produktów</div>
                </div>

                <div className="summary-total">
                  <strong>Suma: {totalPrice.toFixed(2)} zł</strong>
                </div>
              </>
            )}

            {submitError && (
              <div className="error-message">
                {submitError}
              </div>
            )}

            <div className="privacy-policy-agreement" style={{
              margin: '20px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
            }}>
              <input
                type="checkbox"
                id="privacyPolicy"
                checked={privacyPolicyAgreed}
                onChange={(e) => setPrivacyPolicyAgreed(e.target.checked)}
                style={{ margin: 0 }}
              />
              <label htmlFor="privacyPolicy" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                Oświadczam, że zapoznałem się z <a href="https://docs.google.com/document/d/1LcOfKKTDl8BWzzz_p6GFAdWG16EeUfxBvIlCsb9lpeU/edit?tab=t.0" target="_blank" style={{ color: '#007e46', textDecoration: 'underline', display: 'inline' }}>polityką prywatności</a> i akceptuję jej warunki.
              </label>
            </div>



            <div className="button-group">
              <button
                type="button"
                className="add-button"
                onClick={handleSubmit}
                disabled={!privacyPolicyAgreed}
              >
                {orderType === 'catering' ? 'Wyślij' : 'Złóż zamówienie'}
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={handleCloseSummary}
              >
                Wróć
              </button>
            </div>
            <p className="recaptcha-notice" style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '20px',
              textAlign: 'center',
              wordBreak: 'break-word'
            }}>
              Ta strona jest chroniona przez reCAPTCHA, stosowane są <a href="https://policies.google.com/privacy" style={{ display: 'inline' }} title="Polityka Prywatności">Polityka Prywatności</a> oraz <a href="https://policies.google.com/terms" style={{ display: 'inline' }} title="Warunki korzystania z usługi">Warunki korzystania z usługi</a> Google.
            </p>
          </div>
        </div>
      )}

      {submitSuccess && (
        <div className="product-details-overlay">
          <div className="product-details success-popup">
            {orderType === 'catering' ? (
              <>
                <h2 style={{ color: '#28a745' }}>Zamówienie przyjęte</h2>
                <p>Twoja prośba o zamówienie cateringowe została przyjęta.</p>
                <p>Nasz personel skontaktuje się z Tobą wkrótce w celu omówienia szczegółów.</p>
              </>
            ) : (
              <>
                <p>Twoje zamówienie zostało pomyślnie złożone.</p>
                <p>Numer zamówienia: #{orderId}</p>
                <p>Na podany adres e-mail otrzymasz informację o przyjęciu zamówienia, gdy zostanie ono zaakceptowane przez pracownika</p>
              </>
            )}
            <div className="button-group">
              <button
                type="button"
                className="add-button"
                onClick={() => {
                  setSubmitSuccess(false);
                  document.body.style.overflow = 'auto';
                  setName('');
                  setEmail('');
                  setPhone('');
                  setPickupDateTime('');
                  setPickupLocation('');
                  setSelectedProducts([]);
                  setOrderType('individual');
                  setSuggestedDateTime(null);
                  setSelectedLocationSchedule([]);
                }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Trwa składanie zamówienia...</p>
          </div>
        </div>
      )}
    </section>
  );
}